import React, { useCallback, useEffect, useMemo } from 'react';
import { useState } from 'react';
import { BaseError } from 'libs/end-to-end/src/types/errors/base-error';
import { CreateOrUpdateOrganizationRequestBodySchema } from 'libs/end-to-end/src/types/organizations/organization';
import { Organization } from 'libs/end-to-end/src/types/organizations/organization';
import { Setter } from 'libs/end-to-end/src/ui-types';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { TouchTargetBehavior } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { useParams } from 'react-router';
import { SaveIndicatorStatus } from 'apps/sigma2-ui/src/components/SaveIndicator';
import { useSimpleReadWritePattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadWritePattern';
import { useOrganizationLogoUpload } from 'apps/sigma2-ui/src/organizations/lib/useOrganizationLogoUpload';
import { getFileNameFromUrl } from 'apps/sigma2-ui/src/organizations/utils/get-organization-logo-url';
import { getOrganizationLogoUrl } from 'apps/sigma2-ui/src/organizations/utils/get-organization-logo-url';
import { useAPIMetaInformation } from 'apps/sigma2-ui/src/state/meta-information';
import { LogoFileMimeType } from 'libs/end-to-end/src/logos';

export interface OrganizationEditPageHook {
  name: string;
  setName: Setter<string>;
  submitButtonLabel: React.ReactNode;
  saveIndicatorStatus: SaveIndicatorStatus;
  submitButtonBehavior: TouchTargetBehavior;
  submitButtonDisabled: boolean;
  error: BaseError | null;
  saved: boolean;
  manageDatabasesButtonBehavior: TouchTargetBehavior;
  manageDatabasesButtonLabel: string;
  manageEntitlementsButtonBehavior: TouchTargetBehavior;
  manageEntitlementsButtonLabel: string;
  manageConnectionsButtonBehavior: TouchTargetBehavior;
  manageConnectionsButtonLabel: string;
  backButtonLabel: string;
  backButtonBehavior: TouchTargetBehavior;
  uploadedImageUrl: string | null;
  isUploading: boolean;
  setUploadedImageUrl: (url: string | null) => void;
  uploadError: string | null;
  selectedFile: string | null;
  showCropper: boolean;
  dragActive: boolean;
  handleFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleDrag: (e: React.DragEvent) => void;
  handleDrop: (e: React.DragEvent) => void;
  handleCropComplete: (blob: Blob) => void;
  handleRemoveImage: () => void;
  setShowCropper: (show: boolean) => void;
  setSelectedFile: (file: string | null) => void;
  logoMimeType: LogoFileMimeType | null;
  croppedBlob: Blob | null;
  organizationId: string;
}

export const useOrganizationEditPage = (): OrganizationEditPageHook => {
  const { organizationId } = useParams();

  if (!organizationId) {
    throw new Error('organizationId is required');
  }

  const { metaInformation } = useAPIMetaInformation();
  const [name, setName] = useState('');
  const [internalUploadedImageUrl, setUploadedImageUrl] = useState<
    string | null
  >(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [croppedBlob, setCroppedBlob] = useState<Blob | null>(null);
  const [logoMimeType, setLogoMimeType] = useState<LogoFileMimeType | null>(
    null,
  );

  const patternGetReadUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}`;
  }, [organizationId]);

  const patternGetWriteUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}`;
  }, [organizationId]);

  const pattern = useSimpleReadWritePattern({
    getReadUrlFn: patternGetReadUrlFn,
    getWriteUrlFn: patternGetWriteUrlFn,
    writeMethod: 'PUT',
    canRead: true,
    canWrite: name.length > 0,
    writeSchema: CreateOrUpdateOrganizationRequestBodySchema,
    responseSchema: Organization,
  });

  useEffect(() => {
    if (pattern.status !== 'done') {
      return;
    }

    setName(pattern.entity.name);

    if (pattern.entity.logoMimeType && pattern.entity.logoUrl) {
      setLogoMimeType(pattern.entity.logoMimeType);
      setUploadedImageUrl(pattern.entity.logoUrl);
    }
  }, [pattern, organizationId]);

  const uploadedImageUrl = useMemo(() => {
    if (!internalUploadedImageUrl || pattern.status !== 'done') {
      return null;
    }

    // We have only part of the URL, so we need to construct the full URL
    if (
      !internalUploadedImageUrl.startsWith('data:') &&
      !internalUploadedImageUrl.startsWith('blob:')
    ) {
      return (
        getOrganizationLogoUrl({
          organization: pattern.entity,
          publicDynamicAssetsDomainName:
            metaInformation?.publicDynamicAssetsDomainName ?? '',
        }) ?? null
      );
    }

    return internalUploadedImageUrl;
  }, [
    internalUploadedImageUrl,
    metaInformation?.publicDynamicAssetsDomainName,
    pattern,
  ]);

  useEffect(() => {
    return () => {
      if (uploadedImageUrl) {
        URL.revokeObjectURL(uploadedImageUrl);
      }
    };
  }, [uploadedImageUrl]);

  const uploadImageHook = useOrganizationLogoUpload();

  const validateFile = useCallback((file: File) => {
    const validatedType = LogoFileMimeType.safeParse(file.type);
    if (!validatedType.success) {
      alert('Please upload an image file (JPG, PNG, WebP, SVG)');
      return false;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('File size should be less than 5MB');
      return false;
    }
    return true;
  }, []);

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files[0]) {
        const file = event.target.files[0];
        if (validateFile(file)) {
          setSelectedFile(URL.createObjectURL(file));
          setShowCropper(true);
        }
      }
    },
    [validateFile],
  );

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        const file = e.dataTransfer.files[0];
        if (validateFile(file)) {
          setSelectedFile(URL.createObjectURL(file));
          setShowCropper(true);
        }
      }
    },
    [validateFile],
  );

  // Function to handle the crop complete event
  const handleCropComplete = useCallback((blob: Blob) => {
    setCroppedBlob(blob);
    setUploadedImageUrl(URL.createObjectURL(blob));
  }, []);

  const handleRemoveImage = useCallback(() => {
    if (uploadedImageUrl) {
      URL.revokeObjectURL(uploadedImageUrl);
    }
    setUploadedImageUrl(null);
    setSelectedFile(null);
    setCroppedBlob(null);
  }, [uploadedImageUrl]);

  const handleSubmit = useCallback(() => {
    if (!pattern.canWrite || name.length <= 0) {
      return;
    }

    const saveEntityWithLogo = (logoData: {
      logoUrl: string;
      logoMimeType: LogoFileMimeType | undefined;
    }) => {
      pattern.saveEntity({
        name,
        ...logoData,
      });
    };

    const handleNewLogo = () => {
      if (!croppedBlob) {
        return;
      }
      const validatedMimeType = LogoFileMimeType.safeParse(croppedBlob.type);
      if (!validatedMimeType.success) {
        return;
      }

      const newLogoUrl = crypto.randomUUID().substring(0, 8) + '-logo';
      uploadImageHook.uploadLogoImage(
        {
          name,
          id: organizationId,
          logoMimeType: validatedMimeType.data,
          logoUrl: newLogoUrl,
        },
        croppedBlob,
        () =>
          saveEntityWithLogo({
            logoUrl: newLogoUrl,
            logoMimeType: validatedMimeType.data,
          }),
      );
    };

    if (uploadedImageUrl) {
      if (croppedBlob) {
        // Handle new logo upload
        handleNewLogo();
      } else {
        // Update with existing logo
        saveEntityWithLogo({
          logoUrl: getFileNameFromUrl(uploadedImageUrl),
          logoMimeType: logoMimeType || undefined,
        });
      }
    } else {
      // Save without logo
      pattern.saveEntity({ name });
    }
  }, [
    pattern,
    name,
    uploadedImageUrl,
    croppedBlob,
    logoMimeType,
    organizationId,
    uploadImageHook,
  ]);

  return useMemo(() => {
    return {
      name,
      setName,
      saved: pattern.status === 'done' && pattern.saved,
      error: pattern.status === 'error' ? pattern.error : null,
      submitButtonDisabled:
        !pattern.canWrite || name.length <= 0 || isUploading || showCropper,
      submitButtonLabel: 'Save',
      saveIndicatorStatus: pattern.saveIndicatorStatus,
      submitButtonBehavior: {
        type: 'onClick',
        onClick: handleSubmit,
      },
      manageDatabasesButtonLabel: 'Manage Databases',
      manageDatabasesButtonBehavior: {
        type: 'href',
        href: `/organizations/${organizationId}/databases`,
      },
      manageEntitlementsButtonLabel: 'Manage External Entitlements',
      manageEntitlementsButtonBehavior: {
        type: 'href',
        href: `/organizations/${organizationId}/entitlements`,
      },
      manageConnectionsButtonLabel: 'Manage Connections',
      manageConnectionsButtonBehavior: {
        type: 'href',
        href: `/organizations/${organizationId}/connections`,
      },
      backButtonLabel: 'Back',
      backButtonBehavior: {
        type: 'href',
        href: `/organizations`,
      },
      uploadedImageUrl,
      isUploading,
      setUploadedImageUrl,
      uploadError,
      selectedFile,
      showCropper,
      dragActive,
      handleFileSelect,
      handleDrag,
      handleDrop,
      handleCropComplete,
      handleRemoveImage,
      setShowCropper,
      setSelectedFile,
      logoMimeType,
      croppedBlob,
      organizationId,
    };
  }, [
    name,
    pattern,
    organizationId,
    uploadedImageUrl,
    isUploading,
    handleFileSelect,
    handleDrag,
    handleDrop,
    handleCropComplete,
    handleRemoveImage,
    logoMimeType,
    croppedBlob,
    dragActive,
    selectedFile,
    showCropper,
    uploadError,
    handleSubmit,
  ]);
};
